import { useEffect, useRef, useCallback, useState, ReactNode } from 'react'
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { MapContext } from '../../hooks/useMap'
import { useAlertStore } from '../../store/alertStore'
import { useSettingsStore } from '../../store/settingsStore'
import { useIsDesktop } from '../../hooks/useIsDesktop'

const AUSTRALIA_BOUNDS: maplibregl.LngLatBoundsLike = [112.9, -43.7, 153.6, -10.7]

const BAKED_STYLE = import.meta.env.VITE_MAPLIBRE_STYLE ?? 'https://demotiles.maplibre.org/style.json'
const DARK_STYLE = BAKED_STYLE.includes('maptiler.com')
  ? BAKED_STYLE.replace(/\/maps\/[^/]+\//, '/maps/streets-v2-dark/')
  : BAKED_STYLE
const LIGHT_STYLE = BAKED_STYLE.includes('maptiler.com')
  ? BAKED_STYLE.replace(/\/maps\/[^/]+\//, '/maps/streets-v2/')
  : BAKED_STYLE

interface AlertMapProps {
  children?: ReactNode
  className?: string
}

// Collapsed mobile sheet height in px — must match MobileSheet h-36 (144px)
const SHEET_HEIGHT = 144

export default function AlertMap({ children, className = '' }: AlertMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const initializedRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)

  const theme = useSettingsStore((s) => s.theme)
  const initialThemeRef = useRef(theme)
  const isDesktop = useIsDesktop()
  const isDesktopRef = useRef(isDesktop)
  isDesktopRef.current = isDesktop

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return
    initializedRef.current = true

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: initialThemeRef.current === 'dark' ? DARK_STYLE : LIGHT_STYLE,
      bounds: AUSTRALIA_BOUNDS,
      fitBoundsOptions: { padding: 20 },
      attributionControl: { compact: false },
    })

    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    // Scale control is useful on desktop; on mobile it sits behind the bottom sheet
    if (isDesktopRef.current) {
      map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-right')
    }

    map.once('style.load', () => {
      // Offset map camera so the bottom sheet doesn't obscure centred POIs
      if (!isDesktopRef.current) {
        map.setPadding({ top: 0, right: 0, bottom: SHEET_HEIGHT, left: 0 })
      }
      setMapReady(true)
    })

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      initializedRef.current = false
    }
  }, [])

  // Switch style on theme change, skipping the initial render
  const themeChangedRef = useRef(false)
  useEffect(() => {
    if (!themeChangedRef.current) { themeChangedRef.current = true; return }
    // diff: false ensures a full style replacement so style.load always fires
    mapRef.current?.setStyle(theme === 'dark' ? DARK_STYLE : LIGHT_STYLE, { diff: false })
  }, [theme])

  const registerFlyTo = useAlertStore((s) => s.registerFlyTo)
  const registerFitBounds = useAlertStore((s) => s.registerFitBounds)

  const flyTo = useCallback((lon: number, lat: number, zoom = 9) => {
    mapRef.current?.flyTo({ center: [lon, lat], zoom, duration: 1200 })
  }, [])

  const fitBounds = useCallback((bbox: [number, number, number, number], padding = 60) => {
    // On mobile the bottom sheet covers SHEET_HEIGHT px — add that to the bottom padding
    // so the selected polygon isn't obscured behind the sheet.
    const bottomPad = isDesktopRef.current ? padding : padding + SHEET_HEIGHT
    mapRef.current?.fitBounds(bbox, {
      padding: { top: padding, right: padding, bottom: bottomPad, left: padding },
      duration: 1200,
      maxZoom: 16,
    })
  }, [])

  useEffect(() => { registerFlyTo(flyTo) }, [flyTo, registerFlyTo])
  useEffect(() => { registerFitBounds(fitBounds) }, [fitBounds, registerFitBounds])

  return (
    <MapContext.Provider value={{ mapRef, flyTo, mapReady }}>
      <div ref={containerRef} className={className}>
        {children}
      </div>
    </MapContext.Provider>
  )
}
